import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import api from '../../api';
import { theme } from '../../theme';
import { formatSpan } from '../../hooks/useSubathonAdmin';
import { SubathonEventType, SubathonRunRow } from '../../types/subathon';
import {
  ErrorText,
  Muted,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  SurfaceTitle,
} from '../ui';

const TYPE_LABELS: Record<SubathonEventType, string> = {
  follow: 'Follows',
  raid: 'Raids',
  bits: 'Bits',
  sub: 'Subs',
  resub: 'Resubs',
  subgift: 'Subs regaladas',
  cafecito: 'Cafecitos',
  coffee: 'Ko-fi',
  tip: 'Propinas',
  chest: 'Cofres',
  manual: 'Ajustes',
};

const REASONS: Record<string, string> = {
  timeout: 'llegó a cero',
  manual: 'la cortó Euge',
  reset: 'se limpió para otro',
};

const RunCard = styled.div`
  border: 1px solid rgba(255, 20, 147, 0.14);
  border-radius: 12px;
  padding: 0.85rem;
  background: rgba(255, 255, 255, 0.02);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  gap: 0.85rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Head = styled.div`
  h4 {
    margin: 0 0 0.3rem;
    font-size: 0.95rem;
    color: #fff;
  }

  small {
    color: ${theme.colors.textMuted};
    font-size: 0.78rem;
  }
`;

const Lines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.6rem;
  font-size: 0.84rem;
  color: ${theme.colors.textMuted};

  strong {
    color: #fff;
    font-variant-numeric: tabular-nums;
  }
`;

const Breakdown = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.4rem;
`;

const Chip = styled.div`
  border-radius: 9px;
  padding: 0.45rem 0.55rem;
  background: rgba(255, 20, 147, 0.06);
  border: 1px solid rgba(255, 20, 147, 0.14);
  font-size: 0.78rem;
  color: ${theme.colors.textMuted};

  strong {
    display: block;
    color: #fff;
    font-size: 0.9rem;
    font-variant-numeric: tabular-nums;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SubathonHistoryTab: React.FC = () => {
  const [runs, setRuns] = useState<SubathonRunRow[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get('/subathon/history')
      .then(({ data }) => setRuns(data.runs || []))
      .catch((err) => setError(err?.response?.data?.message || 'No se pudo cargar el historial'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Muted>Cargando historial…</Muted>;
  if (error) return <ErrorText>{error}</ErrorText>;

  if (runs.length === 0) {
    return (
      <Surface>
        <SurfaceBody>
          <Muted>
            Todavía no hiciste ningún extensible. Cuando termine el primero vas a ver acá cuánto duró
            y de dónde vino cada minuto.
          </Muted>
        </SurfaceBody>
      </Surface>
    );
  }

  return (
    <Surface>
      <SurfaceHeader>
        <SurfaceTitle>Extensibles anteriores</SurfaceTitle>
      </SurfaceHeader>
      <SurfaceBody>
        <List>
          {runs.map((run) => {
            const duration = run.initialMs + Math.max(0, run.addedMs);
            const breakdown = Object.entries(run.totals.byType || {})
              .filter(([, info]) => (info?.seconds || 0) !== 0)
              .sort((a, b) => (b[1]?.seconds || 0) - (a[1]?.seconds || 0));

            return (
              <RunCard key={run.id}>
                <Head>
                  <h4>{new Date(run.startedAt).toLocaleString()}</h4>
                  <small>
                    {run.endedAt
                      ? `Terminó ${new Date(run.endedAt).toLocaleTimeString()} · ${REASONS[run.endedReason] || run.endedReason}`
                      : 'En curso'}
                  </small>
                  <Lines>
                    <div>
                      Duración total: <strong>{formatSpan(duration)}</strong>
                    </div>
                    <div>
                      Arranque: <strong>{formatSpan(run.initialMs)}</strong>
                    </div>
                    <div>
                      Extendido por la gente: <strong>{formatSpan(run.addedMs - run.manualMs)}</strong>
                    </div>
                    {run.manualMs !== 0 && (
                      <div>
                        Ajustes a mano: <strong>{formatSpan(run.manualMs)}</strong>
                      </div>
                    )}
                    <div>
                      Aportes: <strong>{run.eventsCount}</strong> de{' '}
                      <strong>{run.participants}</strong> personas
                    </div>
                  </Lines>
                </Head>
                <div>
                  {breakdown.length > 0 && (
                    <Breakdown>
                      {breakdown.map(([type, info]) => (
                        <Chip key={type}>
                          {TYPE_LABELS[type as SubathonEventType] || type}
                          <strong>{formatSpan((info?.seconds || 0) * 1000)}</strong>
                          {info?.count} {info?.count === 1 ? 'vez' : 'veces'}
                        </Chip>
                      ))}
                    </Breakdown>
                  )}
                  {run.totals.top.length > 0 && (
                    <Lines>
                      <div style={{ marginTop: '0.4rem' }}>
                        Top:{' '}
                        {run.totals.top
                          .slice(0, 3)
                          .map((t) => `${t.displayName} (${formatSpan(t.seconds * 1000)})`)
                          .join(' · ')}
                      </div>
                    </Lines>
                  )}
                </div>
              </RunCard>
            );
          })}
        </List>
      </SurfaceBody>
    </Surface>
  );
};

export default SubathonHistoryTab;
